import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  Users, 
  Shield, 
  UserCog, 
  Trash2, 
  Loader2, 
  Building2, 
  UserPlus,
  Copy,
  Check,
  AlertTriangle,
  Key,
  Clock,
  X,
  Mail
} from 'lucide-react';

const ROLES = [
  { value: 'super_admin', label: 'Super Admin', description: 'Full access to all schools and features', superOnly: true },
  { value: 'admin', label: 'School Admin', description: 'Full access to their school\'s data' },
  { value: 'specialist', label: 'Interventionist', description: 'Can add/edit students, progress, exports' },
  { value: 'viewer', label: 'Viewer', description: 'View-only access' }
];

const ROLE_COLORS = {
  super_admin: 'bg-red-100 text-red-800',
  admin: 'bg-purple-100 text-purple-800',
  specialist: 'bg-green-100 text-green-800',
  viewer: 'bg-gray-100 text-gray-600'
};

// Helper to get role label for display
const getRoleLabel = (roleValue) => {
  const role = ROLES.find(r => r.value === roleValue);
  return role ? role.label : roleValue?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
};

export default function UsersPage() {
  const { user: currentUser, isSuperAdmin, isAdmin, selectedSchoolId } = useAuth();
  const [users, setUsers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTempPasswordModal, setShowTempPasswordModal] = useState(false);
  const [tempPasswordData, setTempPasswordData] = useState(null);

  useEffect(() => {
    fetchData();
  }, [selectedSchoolId]); // Re-fetch when school selection changes

  const fetchData = async () => {
    try {
      const [usersRes, schoolsRes] = await Promise.all([
        apiService.getUsers(),
        apiService.getSchools()
      ]);
      setUsers(usersRes.data);
      setSchools(schoolsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on selected school (for Super Admin)
  const filteredUsers = isSuperAdmin && selectedSchoolId
    ? users.filter(u => u.school_id === selectedSchoolId || u.role === 'super_admin')
    : users;

  // Get selected school name for display
  const selectedSchool = schools.find(s => s.id === selectedSchoolId);

  const handleRoleChange = async (userId, newRole) => {
    const user = users.find(u => u.id === userId);
    setUpdating(userId);
    try {
      await apiService.updateUserRole(userId, newRole, user?.school_id);
      setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
      toast.success('User role updated');
    } catch (error) {
      console.error('Failed to update role:', error);
      toast.error(error.response?.data?.detail || 'Failed to update role');
    } finally {
      setUpdating(null);
    }
  };

  const handleSchoolChange = async (userId, newSchoolId) => {
    const user = users.find(u => u.id === userId);
    const school = schools.find(s => s.id === newSchoolId);
    setUpdating(userId);
    try {
      await apiService.updateUserRole(userId, user?.role || 'viewer', newSchoolId || null);
      setUsers(users.map(u => u.id === userId ? { 
        ...u, 
        school_id: newSchoolId || null,
        school_name: school?.name || null
      } : u));
      toast.success('User school updated');
    } catch (error) {
      console.error('Failed to update school:', error);
      toast.error(error.response?.data?.detail || 'Failed to update school');
    } finally {
      setUpdating(null);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This cannot be undone.`)) {
      return;
    }

    try {
      await apiService.deleteUser(userId);
      setUsers(users.filter(u => u.id !== userId));
      toast.success('User deleted');
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResetPassword = async (userId, userName) => {
    if (!window.confirm(`Reset password for ${userName}? They will receive a new temporary password.`)) {
      return;
    }

    setUpdating(userId);
    try {
      const response = await apiService.resetUserPassword(userId);
      setTempPasswordData({
        userName,
        tempPassword: response.data.temp_password,
        message: response.data.message
      });
      setShowTempPasswordModal(true);
      // Refresh users to update status
      fetchData();
    } catch (error) {
      console.error('Failed to reset password:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setUpdating(null);
    }
  };

  const handleUserCreated = (userData) => {
    setShowCreateModal(false);
    setTempPasswordData({
      userName: userData.user.name,
      email: userData.user.email,
      tempPassword: userData.temp_password,
      message: userData.message,
      emailStatus: userData.email_status,
      emailMessage: userData.email_message
    });
    setShowTempPasswordModal(true);
    fetchData();
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Filter roles based on current user's role
  const availableRoles = ROLES.filter(r => isSuperAdmin || !r.superOnly);

  // Get schools available for the current admin
  const availableSchools = isSuperAdmin 
    ? schools 
    : schools.filter(s => s.id === currentUser?.school_id);

  if (loading) {
    return (
      <div className="page-container">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#A3A3A3]" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="users-page">
      <div className="page-header flex justify-between items-start">
        <div>
          <h1 className="page-title">User Management</h1>
          <p className="page-subtitle">Manage staff accounts and permissions</p>
        </div>
        <Button 
          className="bg-[#2C4C3B] hover:bg-[#1E3629]"
          onClick={() => setShowCreateModal(true)}
          data-testid="create-user-btn"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Create User
        </Button>
      </div>

      {/* Role Legend */}
      <div className="card mb-6">
        <div className="card-content">
          <h3 className="text-sm font-medium text-[#1C1C1C] mb-3">Role Permissions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {availableRoles.map(role => (
              <div key={role.value} className="flex items-start gap-3 p-3 bg-[#F3F4F1] rounded-lg">
                <div className={`px-2 py-1 rounded text-xs font-medium ${ROLE_COLORS[role.value]}`}>
                  {role.label}
                </div>
                <span className="text-sm text-[#595959]">{role.description}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-[#595959]" />
            <span>{filteredUsers.length} User{filteredUsers.length !== 1 ? 's' : ''}</span>
            {isSuperAdmin && selectedSchoolId && selectedSchool && (
              <span className="text-sm text-[#2C4C3B] bg-[#2C4C3B]/10 px-2 py-0.5 rounded-full ml-2">
                {selectedSchool.name}
              </span>
            )}
          </div>
          {isSuperAdmin && selectedSchoolId && (
            <span className="text-xs text-[#595959]">
              Showing users for selected school + Super Admins
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>School</th>
                <th>Role</th>
                <th>Joined</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 text-[#2C4C3B] flex items-center justify-center font-medium text-sm">
                        {user.name?.charAt(0) || 'U'}
                      </div>
                      <div>
                        <span className="font-medium text-[#1C1C1C]">
                          {user.name}
                          {user.id === currentUser?.id && (
                            <span className="ml-2 text-xs text-[#A3A3A3]">(You)</span>
                          )}
                        </span>
                        {/* Status indicators */}
                        <div className="flex items-center gap-2 mt-0.5">
                          {user.must_change_password && (
                            <span className={`inline-flex items-center gap-1 text-xs ${user.temp_password_expired ? 'text-red-600' : 'text-amber-600'}`}>
                              <Key className="h-3 w-3" />
                              {user.temp_password_expired ? 'Expired' : 'Temp Password'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="text-[#595959]">{user.email}</td>
                  <td>
                    {user.role === 'super_admin' ? (
                      <span className="text-sm text-[#595959] italic">All Schools</span>
                    ) : isSuperAdmin && user.id !== currentUser?.id ? (
                      <Select
                        value={user.school_id || 'none'}
                        onValueChange={(value) => handleSchoolChange(user.id, value === 'none' ? null : value)}
                        disabled={updating === user.id}
                      >
                        <SelectTrigger className="w-40" data-testid={`school-select-${user.id}`}>
                          <SelectValue placeholder="No School" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No School</SelectItem>
                          {schools.map(school => (
                            <SelectItem key={school.id} value={school.id}>
                              {school.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-[#595959]">
                        <Building2 className="h-4 w-4 text-[#A3A3A3]" />
                        {user.school_name || 'No School'}
                      </div>
                    )}
                  </td>
                  <td>
                    {user.id === currentUser?.id ? (
                      <span className={`px-2 py-1 rounded text-xs font-medium ${ROLE_COLORS[user.role]}`}>
                        {ROLES.find(r => r.value === user.role)?.label || user.role}
                      </span>
                    ) : (
                      <Select
                        value={user.role}
                        onValueChange={(value) => handleRoleChange(user.id, value)}
                        disabled={updating === user.id}
                      >
                        <SelectTrigger className="w-36" data-testid={`role-select-${user.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {availableRoles.map(role => (
                            <SelectItem key={role.value} value={role.value}>
                              {role.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                  <td className="text-[#595959] text-sm">
                    {formatDate(user.created_at)}
                  </td>
                  <td className="text-right">
                    {user.id !== currentUser?.id && (
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleResetPassword(user.id, user.name)}
                          disabled={updating === user.id}
                          className="text-[#595959] hover:text-[#1C1C1C] hover:bg-[#F3F4F1]"
                          title="Reset Password"
                          data-testid={`reset-password-${user.id}`}
                        >
                          {updating === user.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Key className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteUser(user.id, user.name)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info Card */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-lg">
        <div className="flex items-start gap-3">
          <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-800">Secure Account Creation</h4>
            <p className="text-sm text-blue-700 mt-1">
              Public registration is disabled. Use the "Create User" button to add new staff members.
              {isSuperAdmin ? (
                <> Super Admins can create users for any school.</>
              ) : (
                <> School Admins can create Interventionist and Viewer accounts for their school.</>
              )}
              {' '}New users will receive a temporary password that expires in 7 days.
            </p>
          </div>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <CreateUserModal 
          schools={availableSchools}
          isSuperAdmin={isSuperAdmin}
          currentUserSchoolId={currentUser?.school_id}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleUserCreated}
        />
      )}

      {/* Temp Password Display Modal */}
      {showTempPasswordModal && tempPasswordData && (
        <TempPasswordModal 
          data={tempPasswordData}
          onClose={() => {
            setShowTempPasswordModal(false);
            setTempPasswordData(null);
          }}
        />
      )}
    </div>
  );
}

// Staff types for user creation (school identity categories, not app function)
const STAFF_TYPES = [
  { value: 'classroom_teacher', label: 'Classroom Teacher' },
  { value: 'school_administrator', label: 'School Administrator' },
  { value: 'speech_language_pathologist', label: 'Speech-Language Pathologist' },
  { value: 'counselor', label: 'Counselor' },
  { value: 'instructional_coach', label: 'Instructional Coach' },
  { value: 'reading_specialist', label: 'Reading Specialist' },
  { value: 'math_specialist', label: 'Math Specialist' },
  { value: 'behavior_specialist', label: 'Behavior Specialist' },
  { value: 'intervention_support_staff', label: 'Intervention Support Staff' },
  { value: 'school_psychologist', label: 'School Psychologist' },
  { value: 'other', label: 'Other' }
];

// Create User Modal Component
function CreateUserModal({ schools, isSuperAdmin, currentUserSchoolId, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'specialist',
    school_id: isSuperAdmin ? '' : currentUserSchoolId,
    override_domain_check: false,
    // Staff linkage fields - default can_provide_interventions to true for most roles
    staff_type: 'classroom_teacher',
    staff_type_other: '',
    can_provide_interventions: true
  });
  const [domainWarning, setDomainWarning] = useState(null);

  // Update defaults when role changes
  const handleRoleChange = (role) => {
    let newStaffType = formData.staff_type;
    let newCanProvide = formData.can_provide_interventions;
    
    if (role === 'specialist') {
      // Intervention Provider: default to classroom_teacher, can_provide = true
      newStaffType = formData.staff_type || 'classroom_teacher';
      newCanProvide = true;
    } else if (role === 'viewer') {
      // Viewer: default to classroom_teacher, can_provide = false
      newStaffType = 'classroom_teacher';
      newCanProvide = false;
    } else if (role === 'admin') {
      // Admin: default to school_administrator, can_provide = true
      newStaffType = formData.staff_type || 'school_administrator';
      newCanProvide = true;
    } else if (role === 'super_admin') {
      // Super Admin shouldn't have staff contact linkage
      newStaffType = null;
      newCanProvide = false;
    }
    
    setFormData({ 
      ...formData, 
      role, 
      staff_type: newStaffType, 
      can_provide_interventions: newCanProvide 
    });
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    
    // Check email domain when school or email changes
    if (field === 'email' || field === 'school_id') {
      checkEmailDomain(
        field === 'email' ? value : formData.email,
        field === 'school_id' ? value : formData.school_id
      );
    }
  };

  const checkEmailDomain = (email, schoolId) => {
    if (!email || !schoolId) {
      setDomainWarning(null);
      return;
    }
    
    const school = schools.find(s => s.id === schoolId);
    const approvedDomains = school?.approved_email_domains || [];
    
    if (approvedDomains.length === 0) {
      setDomainWarning(null);
      return;
    }
    
    const emailDomain = email.split('@')[1]?.toLowerCase();
    const isApproved = approvedDomains.some(d => d.toLowerCase() === emailDomain);
    
    if (!isApproved && emailDomain) {
      setDomainWarning({
        domain: emailDomain,
        approved: approvedDomains
      });
    } else {
      setDomainWarning(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.school_id) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      // Prepare data - only include staff_type if it's set (not for super_admin)
      const submitData = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        school_id: formData.school_id,
        override_domain_check: formData.override_domain_check
      };
      
      // Add staff linkage fields if staff_type is set
      if (formData.staff_type && formData.role !== 'super_admin') {
        submitData.staff_type = formData.staff_type;
        submitData.can_provide_interventions = formData.can_provide_interventions;
        // Include custom staff type if "other" is selected
        if (formData.staff_type === 'other' && formData.staff_type_other) {
          submitData.staff_type_other = formData.staff_type_other.trim();
        }
      }
      
      const response = await apiService.createUser(submitData);
      toast.success('User created successfully!');
      onSuccess(response.data);
    } catch (error) {
      console.error('Failed to create user:', error);
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  // Roles available for creation
  const creatableRoles = isSuperAdmin 
    ? ROLES 
    : ROLES.filter(r => r.value === 'specialist' || r.value === 'viewer');

  // Show staff linkage section (not for super_admin role)
  const showStaffLinkage = formData.role !== 'super_admin';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '550px' }}>
        <div className="modal-header">
          <h2 className="modal-title">Create User Account</h2>
          <button onClick={onClose} className="text-[#A3A3A3] hover:text-[#595959]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body space-y-4">
            <div className="form-group">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Enter full name"
                required
                data-testid="create-user-name"
              />
            </div>

            <div className="form-group">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="Enter email address"
                required
                data-testid="create-user-email"
              />
            </div>

            {domainWarning && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                  <div className="text-sm">
                    <p className="text-amber-800 font-medium">Email domain not approved</p>
                    <p className="text-amber-700">
                      "{domainWarning.domain}" is not in the approved domains: {domainWarning.approved.join(', ')}
                    </p>
                    {isSuperAdmin && (
                      <div className="flex items-center gap-2 mt-2">
                        <Switch
                          checked={formData.override_domain_check}
                          onCheckedChange={(v) => handleChange('override_domain_check', v)}
                          data-testid="override-domain-check"
                        />
                        <span className="text-amber-700">Override domain check (Super Admin)</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="form-group">
              <Label htmlFor="school">School *</Label>
              <Select 
                value={formData.school_id} 
                onValueChange={(v) => handleChange('school_id', v)}
                disabled={!isSuperAdmin}
              >
                <SelectTrigger data-testid="create-user-school">
                  <SelectValue placeholder="Select a school" />
                </SelectTrigger>
                <SelectContent>
                  {schools.map(school => (
                    <SelectItem key={school.id} value={school.id}>
                      {school.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="form-group">
              <Label htmlFor="role">App Role *</Label>
              <Select 
                value={formData.role} 
                onValueChange={(v) => handleRoleChange(v)}
              >
                <SelectTrigger data-testid="create-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {creatableRoles.map(role => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#595959] mt-1">
                {ROLES.find(r => r.value === formData.role)?.description}
              </p>
            </div>

            {/* Staff Linkage Section */}
            {showStaffLinkage && (
              <div className="p-4 bg-[#F3F4F1] border border-[#E5E5E5] rounded-lg space-y-4">
                <div className="flex items-start gap-2">
                  <Building2 className="h-4 w-4 text-[#2C4C3B] mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-[#1C1C1C]">Staff Directory Link</p>
                    <p className="text-xs text-[#595959]">
                      A linked staff contact will be created using this user's email so the person can be assigned to students, interventions, and future messages.
                    </p>
                  </div>
                </div>

                <div className="form-group">
                  <Label htmlFor="staff_type">Staff Type</Label>
                  <Select 
                    value={formData.staff_type || 'classroom_teacher'} 
                    onValueChange={(v) => {
                      // Batch both updates in a single state change to avoid race condition
                      setFormData(prev => ({
                        ...prev,
                        staff_type: v,
                        // Clear custom field when not "other"
                        staff_type_other: v !== 'other' ? '' : prev.staff_type_other
                      }));
                    }}
                  >
                    <SelectTrigger data-testid="create-user-staff-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STAFF_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Custom staff type field when "Other" is selected */}
                {formData.staff_type === 'other' && (
                  <div className="form-group">
                    <Label htmlFor="staff_type_other">Specify Staff Type *</Label>
                    <Input
                      id="staff_type_other"
                      value={formData.staff_type_other || ''}
                      onChange={(e) => handleChange('staff_type_other', e.target.value)}
                      placeholder="e.g., Occupational Therapist"
                      required
                      data-testid="create-user-staff-type-other"
                    />
                  </div>
                )}

                {/* Can Provide Interventions - Prominent Toggle */}
                <div className={`p-3 rounded-lg border-2 ${formData.can_provide_interventions ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-white'}`}>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-[#1C1C1C]">Can provide interventions</p>
                      <p className="text-xs text-[#595959] mt-0.5">
                        Turn this on if this person may be assigned as the provider for a student intervention.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-medium ${!formData.can_provide_interventions ? 'text-[#595959]' : 'text-[#A3A3A3]'}`}>No</span>
                      <Switch
                        checked={formData.can_provide_interventions}
                        onCheckedChange={(v) => handleChange('can_provide_interventions', v)}
                        className="data-[state=checked]:bg-green-600"
                        data-testid="create-user-can-provide"
                      />
                      <span className={`text-xs font-medium ${formData.can_provide_interventions ? 'text-green-700' : 'text-[#A3A3A3]'}`}>Yes</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-blue-700">
                <Clock className="h-4 w-4" />
                <span>A temporary password will be generated (expires in 7 days)</span>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-[#2C4C3B] hover:bg-[#1E3629]"
              disabled={loading || (domainWarning && !formData.override_domain_check)}
              data-testid="create-user-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Create User
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Temp Password Display Modal
function TempPasswordModal({ data, onClose }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(data.tempPassword);
    setCopied(true);
    toast.success('Password copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const getEmailStatusBadge = () => {
    if (!data.emailStatus) return null;
    
    switch (data.emailStatus) {
      case 'success':
        return (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2 text-green-700">
              <Mail className="h-4 w-4" />
              <span className="text-sm font-medium">Welcome email sent to {data.email}</span>
            </div>
          </div>
        );
      case 'failed':
        return (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <div className="flex items-start gap-2 text-red-700">
              <AlertTriangle className="h-4 w-4 mt-0.5" />
              <div className="text-sm">
                <span className="font-medium">Email delivery failed</span>
                <p className="text-red-600 mt-1">Please share the password manually with the user.</p>
              </div>
            </div>
          </div>
        );
      case 'not_configured':
        return (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <div className="flex items-start gap-2 text-blue-700">
              <Mail className="h-4 w-4 mt-0.5" />
              <div className="text-sm">
                <span className="font-medium">Email service not configured</span>
                <p className="text-blue-600 mt-1">Please share the password manually with the user.</p>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
        <div className="p-6">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <Check className="h-8 w-8 text-green-600" />
            </div>
          </div>

          <h2 className="text-xl font-bold text-center text-[#1C1C1C] mb-2">
            {data.email ? 'User Created Successfully' : 'Password Reset Successfully'}
          </h2>
          
          {data.email && (
            <p className="text-center text-[#595959] mb-4">
              Account created for <strong>{data.userName}</strong> ({data.email})
            </p>
          )}

          {/* Email Status Badge */}
          {getEmailStatusBadge()}

          <div className="bg-[#F3F4F1] border border-[#E5E5E5] rounded-lg p-4 mb-4">
            <p className="text-sm text-[#595959] mb-2">Temporary Password:</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-white border border-[#E5E5E5] rounded px-3 py-2 font-mono text-lg">
                {data.tempPassword}
              </code>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleCopy}
                className="shrink-0"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Important:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  {data.emailStatus !== 'success' && (
                    <li>Share this password securely with the user</li>
                  )}
                  <li>This password expires in 7 days</li>
                  <li>User must change it on first login</li>
                </ul>
              </div>
            </div>
          </div>

          <Button 
            className="w-full bg-[#2C4C3B] hover:bg-[#1E3629]"
            onClick={onClose}
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}
